import type {
  CampaignCreateDto,
  CampaignCreateWithChildrenDto,
  CampaignDecisivePointCreateDto,
  CampaignDecisivePointUpdateDto,
  CampaignScenarioCreateDto,
  CampaignScenarioUpdateDto,
  CampaignSelectDto,
  CampaignUpdateDto,
  CampaignVignetteCreateDto,
  CampaignVignetteUpdateDto,
  CampaignsSelectDto,
} from '@@types/api.types';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { queryKeys } from '@queries/keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const campaignKeys = queryKeys.campaigns;

function useInvalidateCampaign() {
  const queryClient = useQueryClient();

  return (params?: { campaignId?: string; studyIds?: string[] }): void => {
    queryClient.invalidateQueries({ queryKey: campaignKeys.getCampaigns });
    if (params?.campaignId) {
      queryClient.invalidateQueries({ queryKey: campaignKeys.getCampaign(params.campaignId) });
    }
    for (const studyId of params?.studyIds ?? []) {
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudy(studyId) });
    }
  };
}

export function useGetCampaigns() {
  const { apiGet } = useApi();

  return useQuery<CampaignSelectDto[], ApiErrorWrapper>({
    queryFn: async () => {
      const response = await apiGet<CampaignsSelectDto>({ path: '/v1/intel/campaigns' });
      return response.campaigns;
    },
    queryKey: campaignKeys.getCampaigns,
  });
}

export function useGetCampaign(params: { campaignId?: string }) {
  const { campaignId } = params;
  const { apiGet } = useApi();

  return useQuery<CampaignSelectDto, ApiErrorWrapper>({
    enabled: Boolean(campaignId),
    queryFn: async () => {
      return await apiGet<CampaignSelectDto>({ path: `/v1/intel/campaigns/${campaignId}` });
    },
    queryKey: campaignId ? campaignKeys.getCampaign(campaignId) : queryKeys.placeholders.default,
  });
}

export function useCreateCampaign() {
  const { apiPost } = useApi();
  const invalidate = useInvalidateCampaign();

  return useMutation<CampaignSelectDto, ApiErrorWrapper, CampaignCreateDto>({
    mutationFn: async (body) => {
      return await apiPost<CampaignSelectDto>({ body, path: '/v1/intel/campaigns' });
    },
    onSuccess: (campaign) => invalidate({ campaignId: campaign.id }),
  });
}

export function useCreateCampaignWithChildren() {
  const { apiPost } = useApi();
  const invalidate = useInvalidateCampaign();

  return useMutation<CampaignSelectDto, ApiErrorWrapper, CampaignCreateWithChildrenDto>({
    mutationFn: async (body) => {
      return await apiPost<CampaignSelectDto>({ body, path: '/v1/intel/campaigns/with-children' });
    },
    onSuccess: (campaign) =>
      invalidate({ campaignId: campaign.id, studyIds: campaign.consumedStudies.map((link) => link.studyId) }),
  });
}

export function useUpdateCampaign() {
  const { apiPatch } = useApi();
  const invalidate = useInvalidateCampaign();

  return useMutation<CampaignSelectDto, ApiErrorWrapper, { campaignId: string; dto: CampaignUpdateDto }>({
    mutationFn: async ({ campaignId, dto }) => {
      return await apiPatch<CampaignSelectDto>({ body: dto, path: `/v1/intel/campaigns/${campaignId}` });
    },
    onSuccess: (campaign) =>
      invalidate({ campaignId: campaign.id, studyIds: campaign.consumedStudies.map((link) => link.studyId) }),
  });
}

export function useDeleteCampaign() {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, ApiErrorWrapper, { campaignId: string; consumedStudyIds: string[] }>({
    mutationFn: async ({ campaignId }) => {
      await apiDelete<void>({ path: `/v1/intel/campaigns/${campaignId}` });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.getCampaigns });
      queryClient.removeQueries({ queryKey: campaignKeys.getCampaign(variables.campaignId) });
      for (const studyId of variables.consumedStudyIds) {
        queryClient.invalidateQueries({ queryKey: queryKeys.meia.getStudy(studyId) });
      }
    },
  });
}

export function useCreateCampaignDecisivePoint() {
  const { apiPost } = useApi();
  const invalidate = useInvalidateCampaign();

  return useMutation<CampaignSelectDto, ApiErrorWrapper, { campaignId: string; dto: CampaignDecisivePointCreateDto }>({
    mutationFn: async ({ campaignId, dto }) => {
      return await apiPost<CampaignSelectDto>({ body: dto, path: `/v1/intel/campaigns/${campaignId}/decisive-points` });
    },
    onSuccess: (campaign) => invalidate({ campaignId: campaign.id }),
  });
}

export function useUpdateCampaignDecisivePoint() {
  const { apiPatch } = useApi();
  const invalidate = useInvalidateCampaign();

  return useMutation<
    CampaignSelectDto,
    ApiErrorWrapper,
    { campaignId: string; decisivePointId: string; dto: CampaignDecisivePointUpdateDto }
  >({
    mutationFn: async ({ campaignId, decisivePointId, dto }) => {
      return await apiPatch<CampaignSelectDto>({
        body: dto,
        path: `/v1/intel/campaigns/${campaignId}/decisive-points/${decisivePointId}`,
      });
    },
    onSuccess: (campaign) => invalidate({ campaignId: campaign.id }),
  });
}

export function useDeleteCampaignDecisivePoint() {
  const { apiDelete } = useApi();
  const invalidate = useInvalidateCampaign();

  return useMutation<void, ApiErrorWrapper, { campaignId: string; decisivePointId: string }>({
    mutationFn: async ({ campaignId, decisivePointId }) => {
      await apiDelete<void>({ path: `/v1/intel/campaigns/${campaignId}/decisive-points/${decisivePointId}` });
    },
    onSuccess: (_, variables) => invalidate({ campaignId: variables.campaignId }),
  });
}

export function useCreateCampaignScenario() {
  const { apiPost } = useApi();
  const invalidate = useInvalidateCampaign();

  return useMutation<CampaignSelectDto, ApiErrorWrapper, { campaignId: string; dto: CampaignScenarioCreateDto }>({
    mutationFn: async ({ campaignId, dto }) => {
      return await apiPost<CampaignSelectDto>({ body: dto, path: `/v1/intel/campaigns/${campaignId}/scenarios` });
    },
    onSuccess: (campaign) => invalidate({ campaignId: campaign.id }),
  });
}

export function useUpdateCampaignScenario() {
  const { apiPatch } = useApi();
  const invalidate = useInvalidateCampaign();

  return useMutation<
    CampaignSelectDto,
    ApiErrorWrapper,
    { campaignId: string; dto: CampaignScenarioUpdateDto; scenarioId: string }
  >({
    mutationFn: async ({ campaignId, dto, scenarioId }) => {
      return await apiPatch<CampaignSelectDto>({
        body: dto,
        path: `/v1/intel/campaigns/${campaignId}/scenarios/${scenarioId}`,
      });
    },
    onSuccess: (campaign) => invalidate({ campaignId: campaign.id }),
  });
}

export function useDeleteCampaignScenario() {
  const { apiDelete } = useApi();
  const invalidate = useInvalidateCampaign();

  return useMutation<void, ApiErrorWrapper, { campaignId: string; scenarioId: string }>({
    mutationFn: async ({ campaignId, scenarioId }) => {
      await apiDelete<void>({ path: `/v1/intel/campaigns/${campaignId}/scenarios/${scenarioId}` });
    },
    onSuccess: (_, variables) => invalidate({ campaignId: variables.campaignId }),
  });
}

export function useCreateCampaignVignette() {
  const { apiPost } = useApi();
  const invalidate = useInvalidateCampaign();

  return useMutation<
    CampaignSelectDto,
    ApiErrorWrapper,
    { campaignId: string; dto: CampaignVignetteCreateDto; scenarioId: string }
  >({
    mutationFn: async ({ campaignId, dto, scenarioId }) => {
      return await apiPost<CampaignSelectDto>({
        body: dto,
        path: `/v1/intel/campaigns/${campaignId}/scenarios/${scenarioId}/vignettes`,
      });
    },
    onSuccess: (campaign) => invalidate({ campaignId: campaign.id }),
  });
}

export function useUpdateCampaignVignette() {
  const { apiPatch } = useApi();
  const invalidate = useInvalidateCampaign();

  return useMutation<
    CampaignSelectDto,
    ApiErrorWrapper,
    { campaignId: string; dto: CampaignVignetteUpdateDto; scenarioId: string; vignetteId: string }
  >({
    mutationFn: async ({ campaignId, dto, scenarioId, vignetteId }) => {
      return await apiPatch<CampaignSelectDto>({
        body: dto,
        path: `/v1/intel/campaigns/${campaignId}/scenarios/${scenarioId}/vignettes/${vignetteId}`,
      });
    },
    onSuccess: (campaign) => invalidate({ campaignId: campaign.id }),
  });
}

export function useDeleteCampaignVignette() {
  const { apiDelete } = useApi();
  const invalidate = useInvalidateCampaign();

  return useMutation<void, ApiErrorWrapper, { campaignId: string; scenarioId: string; vignetteId: string }>({
    mutationFn: async ({ campaignId, scenarioId, vignetteId }) => {
      await apiDelete<void>({
        path: `/v1/intel/campaigns/${campaignId}/scenarios/${scenarioId}/vignettes/${vignetteId}`,
      });
    },
    onSuccess: (_, variables) => invalidate({ campaignId: variables.campaignId }),
  });
}

export function useLinkCampaignStudy() {
  const { apiPost } = useApi();
  const invalidate = useInvalidateCampaign();

  return useMutation<CampaignSelectDto, ApiErrorWrapper, { campaignId: string; studyId: string }>({
    mutationFn: async ({ campaignId, studyId }) => {
      return await apiPost<CampaignSelectDto>({ path: `/v1/intel/campaigns/${campaignId}/studies/${studyId}` });
    },
    onSuccess: (campaign, variables) => invalidate({ campaignId: campaign.id, studyIds: [variables.studyId] }),
  });
}

export function useUnlinkCampaignStudy() {
  const { apiDelete } = useApi();
  const invalidate = useInvalidateCampaign();

  return useMutation<CampaignSelectDto, ApiErrorWrapper, { campaignId: string; studyId: string }>({
    mutationFn: async ({ campaignId, studyId }) => {
      return await apiDelete<CampaignSelectDto>({ path: `/v1/intel/campaigns/${campaignId}/studies/${studyId}` });
    },
    onSuccess: (campaign, variables) => invalidate({ campaignId: campaign.id, studyIds: [variables.studyId] }),
  });
}
